/**
 * 工作流监控命令
 * 查看工作流执行状态、日志和性能报告
 */

import { Command } from 'commander';
import { withErrorHandling } from '../utils/error-handler';
import { WorkflowMonitor } from '../core/workflow-monitor';
import { WorkflowLogger } from '../core/workflow-logger';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';
import path from 'path';
import fs from 'fs-extra';
import ora from 'ora';
import { formatDistanceToNow } from 'date-fns';

export function createWorkflowMonitorCommand(): Command {
  const command = new Command('monitor');
  
  command
    .description('Monitor workflow executions and view logs')
    .addCommand(createMonitorStatusCommand())
    .addCommand(createMonitorLogsCommand())
    .addCommand(createMonitorReportCommand())
    .addCommand(createMonitorLiveCommand())
    .addCommand(createMonitorCleanupCommand());
  
  return command;
}

/**
 * monitor status - 查看运行状态
 */
function createMonitorStatusCommand(): Command {
  const command = new Command('status');
  
  command
    .description('View workflow execution status')
    .option('-r, --running', 'Show only running workflows')
    .option('-c, --completed', 'Show completed workflows')
    .option('-f, --failed', 'Show failed workflows')
    .option('-l, --limit <n>', 'Limit number of results', '20')
    .action(withErrorHandling(async (options) => {
      const monitor = new WorkflowMonitor();
      const runningWorkflows = monitor.getRunningWorkflows();
      
      console.log(colors.header('\n📊 Workflow Execution Status\n'));
      
      if (options.running) {
        console.log(colors.info(`Running workflows: ${runningWorkflows.length}`));
        
        if (runningWorkflows.length > 0) {
          const table = new Table({
            columns: [
              { name: 'executionId', title: 'Execution ID', alignment: 'left' },
              { name: 'workflowId', title: 'Workflow', alignment: 'left' },
              { name: 'startTime', title: 'Started', alignment: 'center' },
              { name: 'duration', title: 'Duration', alignment: 'center' },
              { name: 'nodes', title: 'Nodes', alignment: 'center' }
            ]
          });
          
          runningWorkflows.forEach(executionId => {
            const metrics = monitor.getMetrics(executionId);
            if (metrics) {
              table.addRow({
                executionId: executionId.substring(0, 12) + '...',
                workflowId: metrics.workflowId.substring(0, 20) + '...',
                startTime: formatDistanceToNow(metrics.startTime, { addSuffix: true }),
                duration: formatDuration(Date.now() - metrics.startTime.getTime()),
                nodes: `${Array.from(metrics.nodeMetrics.values()).filter(n => n.status === 'success').length}/${metrics.nodeMetrics.size}`
              });
            }
          });
          
          table.printTable();
        }
      }
      
      // 从日志目录读取历史执行
      if (options.completed || options.failed) {
        const logDir = path.join(process.cwd(), '.workflow', 'logs', 'executions');
        
        if (await fs.pathExists(logDir)) {
          const files = await fs.readdir(logDir);
          const reports = [];
          
          for (const file of files) {
            if (file.endsWith('-report.json')) {
              try {
                const report = await fs.readJSON(path.join(logDir, file));
                if ((options.completed && report.summary.status === 'success') ||
                    (options.failed && report.summary.status === 'failure')) {
                  reports.push(report);
                }
              } catch (error) {
                // 忽略读取错误
              }
            }
          }
          
          if (reports.length > 0) {
            console.log(colors.header(`\n${options.completed ? '✅ Completed' : '❌ Failed'} Workflows:\n`));
            
            const table = new Table({
              columns: [
                { name: 'executionId', title: 'Execution ID', alignment: 'left' },
                { name: 'workflowId', title: 'Workflow', alignment: 'left' },
                { name: 'status', title: 'Status', alignment: 'center' },
                { name: 'endTime', title: 'Completed', alignment: 'center' },
                { name: 'duration', title: 'Duration', alignment: 'center' },
                { name: 'errors', title: 'Errors', alignment: 'center' }
              ]
            });
            
            reports
              .sort((a, b) => new Date(b.summary.endTime).getTime() - new Date(a.summary.endTime).getTime())
              .slice(0, parseInt(options.limit))
              .forEach(report => {
                table.addRow({
                  executionId: report.summary.executionId.substring(0, 12) + '...',
                  workflowId: report.summary.workflowId.substring(0, 20) + '...',
                  status: report.summary.status === 'success' 
                    ? colors.success('SUCCESS') 
                    : colors.error('FAILED'),
                  endTime: formatDistanceToNow(new Date(report.summary.endTime), { addSuffix: true }),
                  duration: formatDuration(report.summary.duration),
                  errors: report.summary.errorCount || 0
                });
              });
            
            table.printTable();
          }
        }
      }
    }));
  
  return command;
}

/**
 * monitor logs - 查看日志
 */
function createMonitorLogsCommand(): Command {
  const command = new Command('logs');
  
  command
    .description('View workflow execution logs')
    .argument('[executionId]', 'Execution ID to view logs for')
    .option('-l, --level <level>', 'Filter by log level', 'info')
    .option('-e, --event <event>', 'Filter by event type')
    .option('-n, --lines <n>', 'Number of lines to show', '50')
    .option('-f, --follow', 'Follow log output')
    .action(withErrorHandling(async (executionId, options) => {
      const logger = new WorkflowLogger();
      
      if (!executionId) {
        // 显示最近的执行列表
        console.log(colors.header('\n📄 Recent Executions:\n'));
        
        const logDir = path.join(process.cwd(), '.workflow', 'logs', 'executions');
        if (await fs.pathExists(logDir)) {
          const files = await fs.readdir(logDir);
          const executions = files
            .filter(f => f.endsWith('.log'))
            .map(f => f.replace('.log', ''))
            .sort()
            .reverse()
            .slice(0, 10);
          
          executions.forEach((exec, index) => {
            console.log(`${index + 1}. ${exec}`);
          });
          
          console.log(colors.info('\nUse "workflow monitor logs <executionId>" to view logs'));
        }
        return;
      }
      
      console.log(colors.header(`\n📋 Logs for Execution: ${executionId}\n`));
      
      if (options.follow) {
        // 实时跟踪日志
        const logPath = path.join(process.cwd(), '.workflow', 'logs', 'executions', `${executionId}.log`);
        
        if (!await fs.pathExists(logPath)) {
          console.log(colors.error('Log file not found'));
          return;
        }
        
        // 使用 tail 命令跟踪日志
        const { spawn } = require('child_process');
        const tail = spawn('tail', ['-f', logPath]);
        
        tail.stdout.on('data', (data: Buffer) => {
          const lines = data.toString().split('\n').filter(line => line.trim());
          lines.forEach(line => {
            try {
              const entry = JSON.parse(line);
              formatLogEntry(entry);
            } catch {
              console.log(line);
            }
          });
        });
        
        tail.stderr.on('data', (data: Buffer) => {
          console.error(colors.error(`Error: ${data}`));
        });
        
        process.on('SIGINT', () => {
          tail.kill();
          process.exit(0);
        });
      } else {
        // 查询历史日志
        const logs = await logger.queryLogs({
          executionId,
          level: options.level,
          event: options.event,
          limit: parseInt(options.lines)
        });
        
        logs.forEach(log => formatLogEntry(log));
        
        if (logs.length === 0) {
          console.log(colors.warning('No logs found'));
        }
      }
    }));
  
  return command;
}

/**
 * monitor report - 生成执行报告
 */
function createMonitorReportCommand(): Command {
  const command = new Command('report');
  
  command
    .description('Generate execution report')
    .argument('[executionId]', 'Execution ID to generate report for')
    .option('-p, --period <period>', 'Report period (today, week, month)', 'today')
    .option('-o, --output <path>', 'Output file path')
    .option('--format <format>', 'Output format (json, html)', 'json')
    .action(withErrorHandling(async (executionId, options) => {
      const monitor = new WorkflowMonitor();
      
      if (executionId) {
        // 生成单个执行的报告
        console.log(colors.info(`Generating report for execution: ${executionId}`));
        
        const reportPath = path.join(process.cwd(), '.workflow', 'logs', 'executions', `${executionId}-report.json`);
        
        if (!await fs.pathExists(reportPath)) {
          console.log(colors.error('Execution report not found'));
          return;
        }
        
        const report = await fs.readJSON(reportPath);
        
        if (options.output) {
          await fs.writeJSON(options.output, report, { spaces: 2 });
          console.log(colors.success(`Report saved to: ${options.output}`));
        } else {
          // 显示报告摘要
          console.log(colors.header('\n📊 Execution Report\n'));
          console.log(`Execution ID: ${report.summary.executionId}`);
          console.log(`Workflow: ${report.summary.workflowId}`);
          console.log(`Status: ${report.summary.status === 'success' ? colors.success('SUCCESS') : colors.error('FAILED')}`);
          console.log(`Duration: ${formatDuration(report.summary.duration)}`);
          console.log(`Nodes: ${report.summary.totalNodes} (✅ ${report.summary.successfulNodes}, ❌ ${report.summary.failedNodes}, ⏭️  ${report.summary.skippedNodes})`);
          console.log(`Errors: ${report.summary.errorCount}`);
          
          if (report.errors && report.errors.length > 0) {
            console.log(colors.header('\n❌ Errors:\n'));
            report.errors.forEach((error: any) => {
              console.log(`[${error.nodeId}] ${error.error}`);
            });
          }
        }
      } else {
        // 生成性能报告
        console.log(colors.info(`Generating performance report for period: ${options.period}`));
        
        const { startDate, endDate } = getPeriodDates(options.period);
        const report = await monitor.generatePerformanceReport(startDate, endDate);
        
        if (options.output) {
          if (options.format === 'html') {
            const html = generateHTMLReport(report);
            await fs.writeFile(options.output, html);
          } else {
            await fs.writeJSON(options.output, report, { spaces: 2 });
          }
          console.log(colors.success(`Report saved to: ${options.output}`));
        } else {
          // 显示性能报告摘要
          console.log(colors.header('\n📈 Performance Report\n'));
          console.log(`Period: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`);
          console.log(`Total Executions: ${report.summary.totalExecutions}`);
          console.log(`Success Rate: ${((report.summary.successfulExecutions / report.summary.totalExecutions) * 100).toFixed(1)}%`);
          console.log(`Average Duration: ${formatDuration(report.summary.averageDuration)}`);
          console.log(`Total Errors: ${report.summary.totalErrors}`);
          
          console.log(colors.header('\n📊 Performance Percentiles:\n'));
          console.log(`P50 (Median): ${formatDuration(report.performance.p50Duration)}`);
          console.log(`P95: ${formatDuration(report.performance.p95Duration)}`);
          console.log(`P99: ${formatDuration(report.performance.p99Duration)}`);
        }
      }
    }));
  
  return command;
}

/**
 * monitor live - 实时监控
 */
function createMonitorLiveCommand(): Command {
  const command = new Command('live');
  
  command
    .description('Live monitoring dashboard')
    .option('-r, --refresh <ms>', 'Refresh interval in milliseconds', '1000')
    .action(withErrorHandling(async (options) => {
      const monitor = new WorkflowMonitor();
      const refreshInterval = parseInt(options.refresh);
      
      console.clear();
      console.log(colors.header('🔴 Live Workflow Monitoring\n'));
      console.log(colors.dim('Press Ctrl+C to exit\n'));
      
      const updateDisplay = () => {
        const runningWorkflows = monitor.getRunningWorkflows();
        
        // 移动光标到顶部
        process.stdout.write('\x1B[3;0H');
        
        // 清除下面的内容
        process.stdout.write('\x1B[J');
        
        console.log(colors.info(`Active Workflows: ${runningWorkflows.length}`));
        console.log(colors.dim('─'.repeat(80)));
        
        if (runningWorkflows.length === 0) {
          console.log(colors.dim('No workflows running'));
        } else {
          runningWorkflows.forEach(metrics => {
            const running = Array.from(metrics.nodeMetrics.values()).filter(n => n.status === 'running');
            const completed = Array.from(metrics.nodeMetrics.values()).filter(n => n.status === 'success');
            const failed = Array.from(metrics.nodeMetrics.values()).filter(n => n.status === 'failure');
            
            console.log(`\n${colors.bold(metrics.workflowId)}`);
            console.log(`Execution: ${metrics.executionId.substring(0, 20)}...`);
            console.log(`Duration: ${formatDuration(Date.now() - metrics.startTime.getTime())}`);
            console.log(`Progress: ${createProgressBar(completed.length, metrics.nodeMetrics.size)} ${completed.length}/${metrics.nodeMetrics.size}`);
            
            if (running.length > 0) {
              console.log(`Running: ${running.map(n => n.nodeName).join(', ')}`);
            }
            
            if (failed.length > 0) {
              console.log(colors.error(`Failed: ${failed.map(n => n.nodeName).join(', ')}`));
            }
            
            if (metrics.resourceUsage) {
              console.log(`CPU: ${metrics.resourceUsage.cpuUsage.toFixed(2)}s | Memory: ${metrics.resourceUsage.memoryUsage.toFixed(2)}MB`);
            }
          });
        }
        
        console.log(colors.dim('\n─'.repeat(80)));
        console.log(colors.dim(`Last updated: ${new Date().toLocaleTimeString()}`));
      };
      
      // 初始显示
      updateDisplay();
      
      // 定期更新
      const interval = setInterval(updateDisplay, refreshInterval);
      
      // 清理
      process.on('SIGINT', () => {
        clearInterval(interval);
        console.clear();
        process.exit(0);
      });
    }));
  
  return command;
}

/**
 * monitor cleanup - 清理旧日志
 */
function createMonitorCleanupCommand(): Command {
  const command = new Command('cleanup');
  
  command
    .description('Cleanup old logs and reports')
    .option('-d, --days <n>', 'Days to keep', '30')
    .option('--dry-run', 'Show what would be deleted without deleting')
    .action(withErrorHandling(async (options) => {
      const logger = new WorkflowLogger();
      const daysToKeep = parseInt(options.days);
      
      console.log(colors.header(`\n🧹 Cleaning up logs older than ${daysToKeep} days\n`));
      
      if (options.dryRun) {
        console.log(colors.warning('DRY RUN - No files will be deleted\n'));
      }
      
      const spinner = ora('Scanning for old logs...').start();
      
      try {
        // TODO: 实现 dry-run 模式
        if (!options.dryRun) {
          await logger.cleanupOldLogs(daysToKeep);
          spinner.succeed('Cleanup completed');
        } else {
          spinner.info('Dry run completed');
        }
      } catch (error) {
        spinner.fail('Cleanup failed');
        throw error;
      }
    }));
  
  return command;
}

/**
 * 格式化日志条目
 */
function formatLogEntry(entry: any): void {
  const time = new Date(entry.timestamp).toLocaleTimeString();
  const level = entry.level.toUpperCase().padEnd(5);
  
  let levelColor = colors.info;
  switch (entry.level) {
    case 'error':
      levelColor = colors.error;
      break;
    case 'warn':
      levelColor = colors.warning;
      break;
    case 'debug':
      levelColor = colors.dim;
      break;
  }
  
  let output = `${colors.dim(time)} ${levelColor(level)} `;
  
  if (entry.nodeId) {
    output += `[${colors.cyan(entry.nodeId.substring(0, 8))}] `;
  }
  
  output += entry.message;
  
  console.log(output);
  
  if (entry.error) {
    console.log(colors.error(`  Error: ${entry.error.message}`));
  }
}

/**
 * 格式化持续时间
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
}

/**
 * 创建进度条
 */
function createProgressBar(current: number, total: number, width: number = 20): string {
  const percentage = total > 0 ? current / total : 0;
  const filled = Math.floor(percentage * width);
  const empty = width - filled;
  
  return colors.success('█'.repeat(filled)) + colors.dim('░'.repeat(empty));
}

/**
 * 获取时间段日期
 */
function getPeriodDates(period: string): { startDate: Date; endDate: Date } {
  const endDate = new Date();
  const startDate = new Date();
  
  switch (period) {
    case 'today':
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'week':
      startDate.setDate(startDate.getDate() - 7);
      break;
    case 'month':
      startDate.setMonth(startDate.getMonth() - 1);
      break;
    default:
      startDate.setDate(startDate.getDate() - 1);
  }
  
  return { startDate, endDate };
}

/**
 * 生成 HTML 报告
 */
function generateHTMLReport(report: any): string {
  return `
<!DOCTYPE html>
<html>
<head>
  <title>Workflow Performance Report</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #333; }
    .summary { background: #f5f5f5; padding: 15px; border-radius: 5px; }
    .metric { margin: 10px 0; }
    .metric-label { font-weight: bold; }
    table { border-collapse: collapse; width: 100%; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #f2f2f2; }
  </style>
</head>
<body>
  <h1>Workflow Performance Report</h1>
  
  <div class="summary">
    <h2>Summary</h2>
    <div class="metric">
      <span class="metric-label">Period:</span> 
      ${report.period.start} - ${report.period.end}
    </div>
    <div class="metric">
      <span class="metric-label">Total Executions:</span> 
      ${report.summary.totalExecutions}
    </div>
    <div class="metric">
      <span class="metric-label">Success Rate:</span> 
      ${((report.summary.successfulExecutions / report.summary.totalExecutions) * 100).toFixed(1)}%
    </div>
    <div class="metric">
      <span class="metric-label">Average Duration:</span> 
      ${formatDuration(report.summary.averageDuration)}
    </div>
  </div>
  
  <h2>Performance Percentiles</h2>
  <table>
    <tr>
      <th>Percentile</th>
      <th>Duration</th>
    </tr>
    <tr>
      <td>P50 (Median)</td>
      <td>${formatDuration(report.performance.p50Duration)}</td>
    </tr>
    <tr>
      <td>P95</td>
      <td>${formatDuration(report.performance.p95Duration)}</td>
    </tr>
    <tr>
      <td>P99</td>
      <td>${formatDuration(report.performance.p99Duration)}</td>
    </tr>
  </table>
  
  <h2>By Workflow</h2>
  <table>
    <tr>
      <th>Workflow ID</th>
      <th>Executions</th>
      <th>Success Rate</th>
      <th>Avg Duration</th>
      <th>Avg Errors</th>
    </tr>
    ${Object.entries(report.byWorkflow).map(([id, stats]: [string, any]) => `
    <tr>
      <td>${id}</td>
      <td>${stats.count}</td>
      <td>${(stats.successRate * 100).toFixed(1)}%</td>
      <td>${formatDuration(stats.averageDuration)}</td>
      <td>${stats.averageErrors.toFixed(1)}</td>
    </tr>
    `).join('')}
  </table>
</body>
</html>
  `;
}